import React, { useEffect, useState, useCallback } from "react";
import API_BASE from "../config";
import ImportWizard from "../ots/ImportWizard";
import InputsParamsTable, { combineSaved, splitValues } from "../ots/InputsParamsTable";
import LaunchRulesEditor from "../ots/LaunchRulesEditor";
import ReportSectionsEditor from "../ots/ReportSectionsEditor";

// Super-admin management for the Off-the-Shelf (OTS) pipeline catalog.
// Left: categories. Right: the selected category's pipelines. Pipelines are
// PLATFORM-OWNED: admins import them here (Git/Zip) using the shared BYOP import
// machinery — decoupled from any user/chat. Workflow: Import -> Validate
// (container build) -> Release. Only released + active pipelines whose build is
// validated appear on the public OTS page, available to all users. Every
// mutation requires a reason (audit trail).
const OTS_URL = `${API_BASE}/api/super-admin/ots`;

function authHeaders(json = true) {
  const h = { Authorization: "Bearer " + (localStorage.getItem("idToken") || "") };
  if (json) h["Content-Type"] = "application/json";
  return h;
}

function fmtDate(v) {
  if (!v) return "";
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
}

const styles = {
  layout: { display: "flex", gap: 16, alignItems: "flex-start" },
  sidebar: { width: 300, flexShrink: 0, borderRight: "1px solid #e2e8f0", paddingRight: 12, maxHeight: "75vh", overflow: "auto" },
  item: (active) => ({
    padding: "9px 10px", borderRadius: 6, cursor: "pointer", fontSize: 13,
    background: active ? "#eef2ff" : "transparent", fontWeight: active ? 600 : 400,
    color: "#1e293b", display: "flex", justifyContent: "space-between", alignItems: "center",
  }),
  main: { flex: 1, minWidth: 0 },
  input: { width: "100%", padding: 8, border: "1px solid #cbd5e1", borderRadius: 6, boxSizing: "border-box", marginTop: 4 },
  textarea: { width: "100%", minHeight: 70, fontSize: 13, padding: 8, border: "1px solid #cbd5e1", borderRadius: 6, boxSizing: "border-box", marginTop: 4 },
  mono: { fontFamily: "monospace", fontSize: 12 },
  btn: { padding: "7px 14px", border: "1px solid #cbd5e1", borderRadius: 6, background: "#fff", cursor: "pointer", fontSize: 13 },
  btnPrimary: { padding: "7px 14px", border: "none", borderRadius: 6, background: "#2563eb", color: "#fff", cursor: "pointer", fontSize: 13 },
  th: { textAlign: "left", padding: 8, borderBottom: "2px solid #e2e8f0", fontSize: 12, color: "#475569" },
  td: { padding: 8, borderBottom: "1px solid #eef2f7", verticalAlign: "top", fontSize: 13 },
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modal: { background: "#fff", borderRadius: 10, padding: 20, width: "85%", maxWidth: 680, maxHeight: "85vh", overflow: "auto" },
  label: { display: "block", fontSize: 12, color: "#475569", margin: "10px 0 2px", fontWeight: 600 },
  pill: (bg) => ({ fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 999, color: "#fff", background: bg }),
  tab: (active) => ({ padding: "6px 12px", borderRadius: 6, cursor: "pointer", fontSize: 13, border: "1px solid #cbd5e1", background: active ? "#2563eb" : "#fff", color: active ? "#fff" : "#1e293b" }),
};

const EMPTY_CAT = { name: "", description: "", icon: "", sort_order: 0, active: "Y", reason: "" };

function validationPill(p) {
  if (!p) return null;
  if (p.validated_final === "Y") return <span style={styles.pill("#16a34a")}>VALIDATED</span>;
  if (p.validation_status === "failed") return <span style={styles.pill("#dc2626")}>FAILED</span>;
  return <span style={styles.pill("#d97706")}>PENDING</span>;
}

export default function OtsPipelines() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [banner, setBanner] = useState("");
  const [selectedCatId, setSelectedCatId] = useState(null);
  const [pipelines, setPipelines] = useState([]);
  const [pipesLoading, setPipesLoading] = useState(false);

  const [catModal, setCatModal] = useState(null); // {mode, data}
  const [catBanner, setCatBanner] = useState("");

  const [showImport, setShowImport] = useState(false);

  const [configModal, setConfigModal] = useState(null); // {pipelineId, display_name, reason}
  const [configValues, setConfigValues] = useState([]);
  const [configRules, setConfigRules] = useState([]); // conditional launch rules
  const [configReport, setConfigReport] = useState(null); // report-section manifest (or null = pipeline default)
  const [configBanner, setConfigBanner] = useState("");

  const [editModal, setEditModal] = useState(null); // {id, category_id, display_name, description, sort_order, active, reason}
  const [editBanner, setEditBanner] = useState("");

  const [delTarget, setDelTarget] = useState(null); // {kind, id, name}
  const [delReason, setDelReason] = useState("");
  const [delBanner, setDelBanner] = useState("");

  const loadCategories = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await fetch(`${OTS_URL}/categories`, { headers: authHeaders(), credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setCategories(data.categories || []);
    } catch (e) { setError("Failed to load categories: " + e.message); }
    finally { setLoading(false); }
  }, []);

  const loadPipelines = useCallback(async (catId) => {
    if (!catId) { setPipelines([]); return; }
    setPipesLoading(true);
    try {
      const res = await fetch(`${OTS_URL}/pipelines?category_id=${encodeURIComponent(catId)}`, { headers: authHeaders(), credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPipelines(data.pipelines || []);
    } catch (e) { setBanner("Failed to load pipelines: " + e.message); }
    finally { setPipesLoading(false); }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { loadPipelines(selectedCatId); }, [selectedCatId, loadPipelines]);

  const selectedCat = categories.find((c) => c.id === selectedCatId) || null;

  /* ------------------------------ categories ----------------------------- */
  async function saveCategory() {
    const { mode, data } = catModal;
    if (!data.name.trim() || !data.reason.trim()) { setCatBanner("Name and reason are required."); return; }
    const url = mode === "create" ? `${OTS_URL}/categories` : `${OTS_URL}/categories/${data.id}`;
    try {
      const res = await fetch(url, { method: mode === "create" ? "POST" : "PUT", headers: authHeaders(), credentials: "include", body: JSON.stringify(data) });
      const out = await res.json();
      if (!res.ok) throw new Error(out.error || `HTTP ${res.status}`);
      setCatModal(null);
      setBanner(mode === "create" ? "Category created." : "Category updated.");
      await loadCategories();
      if (mode === "create" && out.id) setSelectedCatId(out.id);
    } catch (e) { setCatBanner("Save failed: " + e.message); }
  }

  /* ------------------------------- edit ---------------------------------- */
  async function saveEdit() {
    const d = editModal;
    if (!d.reason.trim()) { setEditBanner("A reason is required."); return; }
    if (!d.display_name.trim()) { setEditBanner("Display name cannot be empty."); return; }
    try {
      const res = await fetch(`${OTS_URL}/pipelines/${d.id}`, { method: "PUT", headers: authHeaders(), credentials: "include", body: JSON.stringify(d) });
      const out = await res.json();
      if (!res.ok) throw new Error(out.error || `HTTP ${res.status}`);
      setEditModal(null);
      setBanner("Pipeline updated.");
      await loadPipelines(selectedCatId);
      await loadCategories();
    } catch (e) { setEditBanner("Save failed: " + e.message); }
  }

  /* --------------------- configure (inputs/parameters) ------------------- */
  async function openConfig(p) {
    setConfigBanner("");
    try {
      const res = await fetch(`${OTS_URL}/pipelines/${p.id}/config`, { headers: authHeaders(), credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setConfigValues(combineSaved(data.inputs, data.parameters));
      setConfigRules(Array.isArray(data.launch_rules) ? data.launch_rules : []);
      setConfigReport(data.report_config && typeof data.report_config === "object" ? data.report_config : null);
      setConfigModal({ pipelineId: p.id, display_name: p.display_name, reason: "" });
    } catch (e) { setBanner("Failed to load configuration: " + e.message); }
  }

  async function saveConfig() {
    if (!configModal.reason.trim()) { setConfigBanner("A reason is required."); return; }
    const s = splitValues(configValues);
    if (s.inputs.concat(s.parameters).some((x) => !x.name || !x.flag)) { setConfigBanner("Every row needs a name and a flag."); return; }
    // Drop incomplete rules/effects (no controlling param, no targeted effects).
    const launch_rules = (configRules || [])
      .map((r) => ({ ...r, then: (r.then || []).filter((e) => e && e.target) }))
      .filter((r) => r.when && r.when.param && r.then.length > 0);
    // Report sections: drop rows with no id; null when there are none (pipeline default).
    const rcSections = (configReport && Array.isArray(configReport.sections) ? configReport.sections : [])
      .filter((sec) => sec && String(sec.id || "").trim());
    const report_config = rcSections.length
      ? { schema_version: (configReport && configReport.schema_version) || "1.0", title: configReport && configReport.title, sections: rcSections }
      : null;
    try {
      const res = await fetch(`${OTS_URL}/pipelines/${configModal.pipelineId}/config`, {
        method: "PUT", headers: authHeaders(), credentials: "include",
        body: JSON.stringify({ inputs: s.inputs, parameters: s.parameters, launch_rules, report_config, reason: configModal.reason.trim() }),
      });
      const out = await res.json();
      if (!res.ok) throw new Error(out.error || `HTTP ${res.status}`);
      setConfigModal(null);
      setBanner("Pipeline inputs & parameters updated.");
    } catch (e) { setConfigBanner("Save failed: " + e.message); }
  }

  // Release / unrelease toggle (release is gated server-side on validation).
  async function toggleRelease(p) {
    const next = p.released === "Y" ? "N" : "Y";
    if (next === "Y" && p.validated_final !== "Y") {
      setBanner("Cannot release: this pipeline's container build is not validated yet.");
      return;
    }
    const reason = window.prompt(`Reason for ${next === "Y" ? "releasing" : "unreleasing"} "${p.display_name}":`);
    if (reason == null || !reason.trim()) return;
    try {
      const res = await fetch(`${OTS_URL}/pipelines/${p.id}`, {
        method: "PUT", headers: authHeaders(), credentials: "include",
        body: JSON.stringify({ released: next, reason: reason.trim() }),
      });
      const out = await res.json();
      if (!res.ok) throw new Error(out.error || `HTTP ${res.status}`);
      setBanner(next === "Y" ? "Pipeline released — now live on the Off-the-Shelf page for all users." : "Pipeline unreleased.");
      await loadPipelines(selectedCatId);
    } catch (e) { setBanner("Release change failed: " + e.message); }
  }

  /* -------------------------------- delete ------------------------------- */
  async function doDelete() {
    if (!delReason.trim()) { setDelBanner("A reason is required."); return; }
    const { kind, id } = delTarget;
    const url = kind === "category" ? `${OTS_URL}/categories/${id}` : `${OTS_URL}/pipelines/${id}`;
    try {
      const res = await fetch(url, { method: "DELETE", headers: authHeaders(), credentials: "include", body: JSON.stringify({ reason: delReason.trim() }) });
      const out = await res.json();
      if (!res.ok) throw new Error(out.error || `HTTP ${res.status}`);
      setDelTarget(null); setDelReason("");
      setBanner(kind === "category" ? "Category deleted." : "Pipeline removed.");
      if (kind === "category") { if (id === selectedCatId) setSelectedCatId(null); await loadCategories(); }
      else { await loadPipelines(selectedCatId); await loadCategories(); }
    } catch (e) { setDelBanner("Delete failed: " + e.message); }
  }

  return (
    <div>
      <h1>Off-the-Shelf Pipelines</h1>
      <p style={{ color: "#64748b" }}>
        Manage the turnkey pipeline catalog shown to users. Categories group pipelines. Import a pipeline
        from GitHub or a ZIP (it is downloaded, parsed and validated via a container build), then release it —
        released pipelines are available to <b>all users</b>. Imported pipelines are platform-owned and
        independent of any user. All changes are recorded with an audit trail.
      </p>

      {error && <p style={{ color: "#b91c1c" }}>{error}</p>}
      {banner && (
        <div style={{ padding: 10, marginBottom: 12, background: "#ecfeff", border: "1px solid #a5f3fc", borderRadius: 8 }}>{banner}</div>
      )}

      <div style={{ marginBottom: 10 }}>
        <button style={styles.btnPrimary} onClick={() => { setCatModal({ mode: "create", data: { ...EMPTY_CAT } }); setCatBanner(""); }}>➕ New category</button>
      </div>

      <div style={styles.layout}>
        {/* Category sidebar */}
        <div style={styles.sidebar}>
          {loading && <p>Loading…</p>}
          {!loading && categories.length === 0 && <p style={{ color: "#94a3b8", fontSize: 13 }}>No categories yet.</p>}
          {categories.map((c) => (
            <div key={c.id} style={styles.item(selectedCatId === c.id)} onClick={() => setSelectedCatId(c.id)}>
              <span>{c.icon ? `${c.icon} ` : ""}{c.name} <span style={{ color: "#94a3b8" }}>({c.pipeline_count})</span></span>
              <span style={styles.pill(c.active === "Y" ? "#16a34a" : "#94a3b8")}>{c.active === "Y" ? "ON" : "OFF"}</span>
            </div>
          ))}
        </div>

        {/* Main panel */}
        <div style={styles.main}>
          {!selectedCat && <p style={{ color: "#94a3b8" }}>Select a category on the left, or create a new one.</p>}

          {selectedCat && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <h3 style={{ margin: "0 0 4px" }}>{selectedCat.icon ? `${selectedCat.icon} ` : ""}{selectedCat.name}</h3>
                <div style={{ display: "flex", gap: 6 }}>
                  <button style={styles.btn} onClick={() => { setCatModal({ mode: "edit", data: { ...EMPTY_CAT, ...selectedCat, reason: "" } }); setCatBanner(""); }}>Edit</button>
                  <button style={{ ...styles.btn, color: "#b91c1c" }} onClick={() => { setDelTarget({ kind: "category", id: selectedCat.id, name: selectedCat.name }); setDelReason(""); setDelBanner(""); }}>Delete</button>
                </div>
              </div>
              {selectedCat.description && <div style={{ color: "#64748b", fontSize: 13, marginBottom: 6 }}>{selectedCat.description}</div>}
              {selectedCat.updated_by_email && (
                <div style={{ color: "#cbd5e1", fontSize: 12, marginBottom: 12 }}>last changed by {selectedCat.updated_by_email} · {fmtDate(selectedCat.updated_at)}</div>
              )}

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
                <h4 style={{ margin: 0 }}>Pipelines</h4>
                <button style={styles.btnPrimary} onClick={() => setShowImport(true)}>⬇ Import pipeline</button>
              </div>

              {pipesLoading && <p>Loading pipelines…</p>}
              {!pipesLoading && pipelines.length === 0 && (
                <p style={{ color: "#94a3b8", fontSize: 13 }}>No pipelines in this category yet — it shows as “Coming Soon” to users. Import one to get started.</p>
              )}
              {pipelines.length > 0 && (
                <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8 }}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Pipeline</th>
                      <th style={styles.th}>Source</th>
                      <th style={styles.th}>Validation</th>
                      <th style={styles.th}>Active</th>
                      <th style={styles.th}>Released</th>
                      <th style={styles.th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pipelines.map((p) => (
                      <tr key={p.id}>
                        <td style={styles.td}><b>{p.display_name}</b>{p.description && <div style={{ color: "#94a3b8", fontSize: 12 }}>{String(p.description).slice(0, 140)}</div>}</td>
                        <td style={styles.td}>
                          {p.byop_github_url ? <a href={p.byop_github_url} target="_blank" rel="noreferrer">{p.byop_short_name || p.byop_github_url}</a> : (p.byop_short_name || "—")}
                          <div style={{ color: "#cbd5e1", fontSize: 11 }}>build #{p.byop_workflow_id}</div>
                        </td>
                        <td style={styles.td}>{validationPill(p)}{p.validation_status === "failed" && <div style={{ color: "#dc2626", fontSize: 11 }}>build failed</div>}</td>
                        <td style={styles.td}><span style={styles.pill(p.active === "Y" ? "#16a34a" : "#94a3b8")}>{p.active === "Y" ? "ON" : "OFF"}</span></td>
                        <td style={styles.td}>
                          <button
                            style={{ ...styles.btn, padding: "3px 8px", color: p.released === "Y" ? "#16a34a" : "#475569", opacity: (p.released !== "Y" && p.validated_final !== "Y") ? 0.5 : 1 }}
                            title={p.validated_final === "Y" ? "" : "Container build must be validated before release"}
                            onClick={() => toggleRelease(p)}
                          >
                            {p.released === "Y" ? "● Released" : "○ Release"}
                          </button>
                        </td>
                        <td style={styles.td}>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button style={styles.btn} onClick={() => openConfig(p)} title="Edit inputs, parameters & descriptions">Configure</button>
                            <button style={styles.btn} onClick={() => { setEditBanner(""); setEditModal({ id: p.id, category_id: p.category_id, display_name: p.display_name, description: p.description || "", instructions: p.instructions || "", sort_order: p.sort_order, active: p.active, reason: "" }); }}>Edit</button>
                            <button style={{ ...styles.btn, color: "#b91c1c" }} onClick={() => { setDelTarget({ kind: "pipeline", id: p.id, name: p.display_name }); setDelReason(""); setDelBanner(""); }}>Remove</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>

      {/* Category modal */}
      {catModal && (
        <div style={styles.modalOverlay} onClick={() => setCatModal(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>{catModal.mode === "create" ? "New category" : "Edit category"}</h3>
            {catBanner && <div style={{ color: "#b91c1c", marginBottom: 8 }}>{catBanner}</div>}
            <label style={styles.label}>Name</label>
            <input style={styles.input} value={catModal.data.name} onChange={(e) => setCatModal({ ...catModal, data: { ...catModal.data, name: e.target.value } })} />
            <label style={styles.label}>Description</label>
            <textarea style={styles.textarea} value={catModal.data.description || ""} onChange={(e) => setCatModal({ ...catModal, data: { ...catModal.data, description: e.target.value } })} />
            <label style={styles.label}>Icon (emoji, optional)</label>
            <input style={styles.input} value={catModal.data.icon || ""} onChange={(e) => setCatModal({ ...catModal, data: { ...catModal.data, icon: e.target.value } })} />
            <label style={styles.label}>Sort order</label>
            <input type="number" style={styles.input} value={catModal.data.sort_order} onChange={(e) => setCatModal({ ...catModal, data: { ...catModal.data, sort_order: e.target.value } })} />
            <label style={styles.label}>Active</label>
            <select style={styles.input} value={catModal.data.active} onChange={(e) => setCatModal({ ...catModal, data: { ...catModal.data, active: e.target.value } })}>
              <option value="Y">Yes — visible to users</option>
              <option value="N">No — hidden</option>
            </select>
            <label style={styles.label}>Reason (required)</label>
            <input style={styles.input} value={catModal.data.reason} onChange={(e) => setCatModal({ ...catModal, data: { ...catModal.data, reason: e.target.value } })} />
            <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
              <button style={styles.btnPrimary} onClick={saveCategory}>{catModal.mode === "create" ? "Create" : "Save"}</button>
              <button style={styles.btn} onClick={() => setCatModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Import wizard (full BYOP multi-step import, platform-owned) */}
      {showImport && selectedCat && (
        <div style={styles.modalOverlay}>
          <div style={{ background: "#f8fafc", borderRadius: 10, width: "97%", maxWidth: 1400, maxHeight: "94vh", overflow: "auto" }} onClick={(e) => e.stopPropagation()}>
            <ImportWizard
              categoryId={selectedCat.id}
              categoryName={selectedCat.name}
              onCancel={() => setShowImport(false)}
              onDone={async (fin) => {
                setShowImport(false);
                setBanner((fin && fin.message) || "Pipeline imported.");
                await loadPipelines(selectedCatId);
                await loadCategories();
              }}
            />
          </div>
        </div>
      )}

      {/* Edit pipeline modal */}
      {editModal && (
        <div style={styles.modalOverlay} onClick={() => setEditModal(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Edit pipeline</h3>
            {editBanner && <div style={{ color: "#b91c1c", marginBottom: 8 }}>{editBanner}</div>}
            <label style={styles.label}>Display name</label>
            <input style={styles.input} value={editModal.display_name} onChange={(e) => setEditModal({ ...editModal, display_name: e.target.value })} />
            <label style={styles.label}>Description (shown on the card)</label>
            <textarea style={styles.textarea} value={editModal.description || ""} onChange={(e) => setEditModal({ ...editModal, description: e.target.value })} />
            <label style={styles.label}>Instructions / how to run (Markdown — shown on the launch page)</label>
            <textarea style={{ ...styles.textarea, minHeight: 220, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12.5 }} placeholder="Markdown. Explain aligner choices, chemistry/protocol, entry points, reference requirements, read-layout caveats, etc. Leave blank to hide the section." value={editModal.instructions || ""} onChange={(e) => setEditModal({ ...editModal, instructions: e.target.value })} />
            <label style={styles.label}>Category</label>
            <select style={styles.input} value={editModal.category_id} onChange={(e) => setEditModal({ ...editModal, category_id: e.target.value })}>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <label style={styles.label}>Sort order</label>
            <input type="number" style={styles.input} value={editModal.sort_order} onChange={(e) => setEditModal({ ...editModal, sort_order: e.target.value })} />
            <label style={styles.label}>Active</label>
            <select style={styles.input} value={editModal.active} onChange={(e) => setEditModal({ ...editModal, active: e.target.value })}>
              <option value="Y">Yes</option>
              <option value="N">No — disabled</option>
            </select>
            <label style={styles.label}>Reason (required)</label>
            <input style={styles.input} value={editModal.reason} onChange={(e) => setEditModal({ ...editModal, reason: e.target.value })} />
            <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
              <button style={styles.btnPrimary} onClick={saveEdit}>Save</button>
              <button style={styles.btn} onClick={() => setEditModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Configure (inputs & parameters) modal */}
      {configModal && (
        <div style={styles.modalOverlay} onClick={() => setConfigModal(null)}>
          <div style={{ ...styles.modal, maxWidth: 1340 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Configure “{configModal.display_name}”</h3>
            <p style={{ color: "#64748b", fontSize: 13, marginTop: 0 }}>
              Edit the pipeline's inputs, parameters and their <b>descriptions</b> (shown to users at launch).
              Changes take effect on the next launch (no container rebuild). Requires a reason.
            </p>
            {configBanner && <div style={{ color: "#b91c1c", marginBottom: 8 }}>{configBanner}</div>}
            <InputsParamsTable values={configValues} setValues={setConfigValues} />
            <LaunchRulesEditor rows={configValues} rules={configRules} setRules={setConfigRules} />
            <ReportSectionsEditor config={configReport} setConfig={setConfigReport} />
            <label style={styles.label}>Reason (required)</label>
            <input style={styles.input} value={configModal.reason} onChange={(e) => setConfigModal({ ...configModal, reason: e.target.value })} />
            <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
              <button style={styles.btnPrimary} onClick={saveConfig}>Save</button>
              <button style={styles.btn} onClick={() => setConfigModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete modal */}
      {delTarget && (
        <div style={styles.modalOverlay} onClick={() => setDelTarget(null)}>
          <div style={{ ...styles.modal, maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>{delTarget.kind === "category" ? "Delete category" : "Remove pipeline"}</h3>
            <p style={{ fontSize: 14 }}>
              {delTarget.kind === "category" ? "Delete" : "Remove"} <b>{delTarget.name}</b>?
              {delTarget.kind === "category" && " All pipelines in this category will also be removed from the catalog."}
              {" "}This is recorded in the audit log.
            </p>
            {delBanner && <div style={{ color: "#b91c1c", marginBottom: 8 }}>{delBanner}</div>}
            <input style={styles.input} placeholder="Reason (required)" value={delReason} onChange={(e) => setDelReason(e.target.value)} />
            <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
              <button style={{ ...styles.btnPrimary, background: "#b91c1c" }} onClick={doDelete}>{delTarget.kind === "category" ? "Delete" : "Remove"}</button>
              <button style={styles.btn} onClick={() => setDelTarget(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
